import { ZODIAC, type ZodiacAnimal } from '@tg/shared';
import { ART_PAL, body, edge, mix, shade, tint, type ArtPal } from './covers/kit';
import { cheeks, eye, ink, smile, type AvatarFace } from './faceKit';

/*
 * Twelve zodiac avatars in the style of a01…a09 (for every language): 128×128, one light source top-left,
 * gradient volume + soft outline, clipped to the central circle by avatarMarkup (which adds the blick).
 * Taboos (docs/art-prompts.md): no green headwear, no clocks, no white-and-black mourning pairs, pig and dog
 * friendly and neutral, no characters in the art. The palette green is never used here.
 */
export const ZODIAC_IDS = ['z01', 'z02', 'z03', 'z04', 'z05', 'z06', 'z07', 'z08', 'z09', 'z10', 'z11', 'z12'] as const;
export type ZodiacId = (typeof ZODIAC_IDS)[number];

export const ZODIAC_ANIMAL = Object.fromEntries(ZODIAC_IDS.map((id, i) => [id, ZODIAC[i]])) as Record<ZodiacId, ZodiacAnimal>;
export const zodiacIdOf = (a: ZodiacAnimal): ZodiacId => ZODIAC_IDS[ZODIAC.indexOf(a)] as ZodiacId;

/** Sunny gold: the light-theme gold is brownish on details, so the light theme takes the bright gold slightly deeper. */
const sun = (P: ArtPal) => (P.dark ? P.gold : shade(ART_PAL.dark.gold, 0.06));

/** Main-colour volume (the `h` gradient of avatarMarkup) with the soft outline. */
const vol = (p: string, c: string, P: ArtPal, w = 2.5) => ` fill="url(#${p}h)" stroke="${edge(c, P)}" stroke-width="${w}"`;
/** Secondary-colour volume: own gradient `id` of colour `c` + outline. */
const sub = (id: string, c: string, P: ArtPal, w = 2) => ` fill="url(#${id})" stroke="${edge(c, P)}" stroke-width="${w}" stroke-linejoin="round"`;
const nostrils = (x1: number, x2: number, y: number, P: ArtPal, rx = 3, ry = 4) =>
  `<ellipse cx="${x1}" cy="${y}" rx="${rx}" ry="${ry}" fill="${ink(P)}" fill-opacity=".8"/><ellipse cx="${x2}" cy="${y}" rx="${rx}" ry="${ry}" fill="${ink(P)}" fill-opacity=".8"/>`;

export const ZODIAC_FACES: Record<ZodiacId, AvatarFace> = {
  // rat — lavender-grey, big round ears, pointed muzzle, whiskers, two little teeth
  z01: {
    c: (P) => tint(P.purple, P.dark ? 0.3 : 0.12),
    draw: (P, p, c) =>
      `<circle cx="32" cy="42" r="20"${vol(p, c, P)}/><circle cx="96" cy="42" r="20"${vol(p, c, P)}/>` +
      `<circle cx="33" cy="43" r="11" fill="${tint(P.pink, 0.2)}"/><circle cx="95" cy="43" r="11" fill="${tint(P.pink, 0.2)}"/>` +
      `<path d="M64 114Q38 110 30 84Q24 54 64 50Q104 54 98 84Q90 110 64 114Z"${vol(p, c, P)} stroke-linejoin="round"/>` +
      `<ellipse cx="64" cy="100" rx="16" ry="11" fill="${tint(c, 0.55)}"/>` +
      eye(50, 78, 6, P) + eye(78, 78, 6, P) +
      `<path d="M44 96L22 92M44 101L24 106M84 96L106 92M84 101L104 106" stroke="${ink(P)}" stroke-opacity=".5" stroke-width="2" stroke-linecap="round"/>` +
      `<rect x="60" y="103" width="8" height="7" rx="2" fill="#FFFFFF" stroke="${edge(c, P)}" stroke-width="1"/>` +
      `<ellipse cx="64" cy="96" rx="6" ry="4.5" fill="${P.pink}" stroke="${edge(P.pink, P)}" stroke-width="1"/>` +
      cheeks(90, P),
  },
  // ox — chocolate brown, cream horns, pink muzzle with nostrils, forehead tuft
  z02: {
    c: (P) => shade(P.orange, P.dark ? 0.42 : 0.38),
    draw: (P, p, c) =>
      body(`${p}x`, tint(P.gold, 0.55), 0.5, 0.3) +
      body(`${p}m`, tint(P.pink, 0.35), 0.4, 0.2) +
      `<path d="M38 50Q16 44 14 20Q26 34 44 38Z"${sub(`${p}x`, P.gold, P)}/><path d="M90 50Q112 44 114 20Q102 34 84 38Z"${sub(`${p}x`, P.gold, P)}/>` +
      `<ellipse cx="22" cy="62" rx="14" ry="7" transform="rotate(20 22 62)"${vol(p, c, P, 2)}/><ellipse cx="106" cy="62" rx="14" ry="7" transform="rotate(-20 106 62)"${vol(p, c, P, 2)}/>` +
      `<rect x="30" y="38" width="68" height="74" rx="32"${vol(p, c, P)}/>` +
      `<path d="M52 42Q58 30 64 42Q70 30 76 42Q70 50 64 46Q58 50 52 42Z" fill="${shade(c, 0.35)}"/>` +
      `<ellipse cx="64" cy="96" rx="26" ry="17"${sub(`${p}m`, P.pink, P)}/>` +
      nostrils(55, 73, 96, P) +
      eye(49, 70, 6, P) + eye(79, 70, 6, P),
  },
  // tiger — orange, round ears, three forehead stripes (no characters), cream muzzle
  z03: {
    c: (P) => P.orange,
    draw: (P, p, c) => {
      const st = shade(c, P.dark ? 0.55 : 0.5);
      const cream = tint(P.gold, P.dark ? 0.7 : 0.8);
      return (
        `<circle cx="34" cy="42" r="14"${vol(p, c, P)}/><circle cx="94" cy="42" r="14"${vol(p, c, P)}/>` +
        `<circle cx="35" cy="43" r="7" fill="${cream}"/><circle cx="93" cy="43" r="7" fill="${cream}"/>` +
        `<ellipse cx="64" cy="76" rx="42" ry="38"${vol(p, c, P)}/>` +
        `<path d="M64 40L60 50L68 50Z M50 43L50 54L57 51Z M78 43L78 54L71 51Z" fill="${st}"/>` +
        `<path d="M22 70L36 73L22 77Z M24 84L36 84L25 90Z M106 70L92 73L106 77Z M104 84L92 84L103 90Z" fill="${st}"/>` +
        `<path d="M64 84Q44 82 42 96Q44 110 64 108Q84 110 86 96Q84 82 64 84Z" fill="${cream}"/>` +
        eye(49, 70, 6, P) + eye(79, 70, 6, P) +
        `<path d="M57 86L71 86L64 93Z" fill="${P.pink}" stroke="${edge(P.pink, P)}" stroke-width="1.5" stroke-linejoin="round"/>` +
        `<path d="M64 93V98M56 99Q64 105 64 98Q64 105 72 99" fill="none" stroke="${ink(P)}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`
      );
    },
  },
  // rabbit — pale pink, one ear folded, buck teeth (differs from the a05 bunny: colour, ears, teeth)
  z04: {
    c: (P) => tint(P.pink, P.dark ? 0.55 : 0.5),
    draw: (P, p, c) =>
      `<ellipse cx="48" cy="32" rx="11" ry="27" transform="rotate(-10 48 32)"${vol(p, c, P)}/>` +
      `<ellipse cx="48" cy="34" rx="5" ry="18" transform="rotate(-10 48 34)" fill="${P.pink}" fill-opacity=".7"/>` +
      `<path d="M74 52Q72 22 88 14Q104 10 106 22Q106 32 88 36Q82 42 84 54Z"${vol(p, c, P)} stroke-linejoin="round"/>` +
      `<path d="M82 42Q84 26 94 20Q100 20 98 26Q92 32 86 36Z" fill="${P.pink}" fill-opacity=".7"/>` +
      `<path d="M64 48Q98 48 100 82Q98 114 64 114Q30 114 28 82Q30 48 64 48Z"${vol(p, c, P)}/>` +
      eye(51, 78, 6, P) + eye(77, 78, 6, P) +
      `<path d="M64 95L64 99M58 101Q64 96 70 101" fill="none" stroke="${ink(P)}" stroke-width="3" stroke-linecap="round"/>` +
      `<rect x="59" y="100" width="10" height="9" rx="2" fill="#FFFFFF" stroke="${edge(c, P)}" stroke-width="1.2"/><path d="M64 100V109" stroke="${edge(c, P)}" stroke-width="1.2"/>` +
      `<path d="M59 91L69 91L64 96Z" fill="${P.pink}" stroke="${edge(P.pink, P)}" stroke-width="1.2" stroke-linejoin="round"/>` +
      cheeks(92, P),
  },
  // dragon — red, golden antlers, whiskers and brows, lighter snout with nostrils
  z05: {
    c: (P) => P.red,
    draw: (P, p, c) => {
      const g = sun(P);
      return (
        body(`${p}x`, g, 0.45, 0.25) +
        `<path d="M46 50L40 28L30 24L38 22L34 10L44 20L50 14L48 30L54 48Z"${sub(`${p}x`, P.gold, P)}/>` +
        `<path d="M82 50L88 28L98 24L90 22L94 10L84 20L78 14L80 30L74 48Z"${sub(`${p}x`, P.gold, P)}/>` +
        `<path d="M26 64L12 56L20 72Z M102 64L116 56L108 72Z"${sub(`${p}x`, P.gold, P)}/>` +
        `<path d="M64 38Q100 38 104 70Q106 90 92 100Q80 116 64 116Q48 116 36 100Q22 90 24 70Q28 38 64 38Z"${vol(p, c, P)}/>` +
        `<path d="M58 38L64 30L70 38L64 46Z" fill="${g}" stroke="${edge(P.gold, P)}" stroke-width="1.5" stroke-linejoin="round"/>` +
        `<ellipse cx="64" cy="98" rx="26" ry="16" fill="${tint(c, 0.3)}"/>` +
        nostrils(55, 73, 95, P, 3, 3.5) +
        `<path d="M40 100Q20 102 14 120M88 100Q108 102 114 120" fill="none" stroke="${g}" stroke-width="3.5" stroke-linecap="round"/>` +
        `<path d="M38 58Q47 51 58 58M70 58Q81 51 90 58" fill="none" stroke="${g}" stroke-width="4" stroke-linecap="round"/>` +
        eye(49, 70, 6.5, P) + eye(79, 70, 6.5, P) +
        smile(64, 106, 7, P)
      );
    },
  },
  // snake — sky blue coil, golden diamond pattern, pink forked tongue
  z06: {
    c: (P) => P.sky,
    draw: (P, p, c) =>
      `<path d="M-4 112Q30 88 64 104Q96 120 132 96" fill="none" stroke="${edge(c, P)}" stroke-width="27" stroke-linecap="round"/>` +
      `<path d="M-4 112Q30 88 64 104Q96 120 132 96" fill="none" stroke="url(#${p}h)" stroke-width="22" stroke-linecap="round"/>` +
      `<path d="M20 104L26 100L32 104L26 108Z M104 108L110 104L116 108L110 112Z" fill="${sun(P)}"/>` +
      `<path d="M64 26Q102 28 100 66Q98 96 64 98Q30 96 28 66Q26 28 64 26Z"${vol(p, c, P)}/>` +
      `<path d="M64 32L72 42L64 52L56 42Z" fill="${sun(P)}" stroke="${edge(P.gold, P)}" stroke-width="1.5" stroke-linejoin="round"/>` +
      `<circle cx="46" cy="42" r="3.5" fill="${sun(P)}"/><circle cx="82" cy="42" r="3.5" fill="${sun(P)}"/>` +
      `<path d="M64 88V100M64 100L59 106M64 100L69 106" fill="none" stroke="${P.pink}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` +
      eye(50, 64, 7, P) + eye(78, 64, 7, P) +
      cheeks(78, P) + smile(64, 84, 9, P),
  },
  // horse — chestnut, golden mane and forelock, light blaze, long muzzle
  z07: {
    c: (P) => shade(mix(P.orange, P.red, 0.3), P.dark ? 0.2 : 0.12),
    draw: (P, p, c) => {
      const g = sun(P);
      return (
        body(`${p}x`, g, 0.45, 0.25) +
        `<path d="M40 44L34 16L56 34Z"${vol(p, c, P)} stroke-linejoin="round"/><path d="M88 44L94 16L72 34Z"${vol(p, c, P)} stroke-linejoin="round"/>` +
        `<path d="M40 36L37 24L48 32Z M88 36L91 24L80 32Z" fill="${tint(P.pink, 0.2)}" fill-opacity=".8"/>` +
        `<path d="M90 36Q112 52 110 86Q108 104 100 116Q98 90 88 74Z"${sub(`${p}x`, P.gold, P)}/>` +
        `<path d="M64 30Q94 30 94 62Q94 80 90 94Q86 118 64 118Q42 118 38 94Q34 80 34 62Q34 30 64 30Z"${vol(p, c, P)}/>` +
        `<path d="M64 48Q70 70 68 90L60 90Q58 70 64 48Z" fill="${tint(c, 0.7)}" fill-opacity=".9"/>` +
        `<path d="M44 38Q56 22 66 26Q82 22 86 38Q76 34 72 46Q66 36 60 48Q54 38 44 38Z"${sub(`${p}x`, P.gold, P)}/>` +
        `<ellipse cx="64" cy="102" rx="22" ry="15" fill="${tint(c, 0.35)}"/>` +
        nostrils(55, 73, 100, P, 3, 4) +
        eye(47, 66, 6, P) + eye(81, 66, 6, P) + smile(64, 110, 5, P)
      );
    },
  },
  // goat — cream, curled golden-brown horns, side ears, small beard
  z08: {
    c: (P) => tint(sun(P), P.dark ? 0.62 : 0.55),
    draw: (P, p, c) => {
      const h = shade(P.gold, P.dark ? 0.25 : 0.1);
      return (
        body(`${p}x`, h, 0.35, 0.3) +
        `<path d="M50 44Q38 22 22 30Q12 40 20 54Q26 46 32 44Q38 40 46 52Z"${sub(`${p}x`, h, P)}/>` +
        `<path d="M78 44Q90 22 106 30Q116 40 108 54Q102 46 96 44Q90 40 82 52Z"${sub(`${p}x`, h, P)}/>` +
        `<ellipse cx="26" cy="68" rx="14" ry="7" transform="rotate(25 26 68)"${vol(p, c, P, 2)}/><ellipse cx="102" cy="68" rx="14" ry="7" transform="rotate(-25 102 68)"${vol(p, c, P, 2)}/>` +
        `<ellipse cx="28" cy="69" rx="7" ry="3" transform="rotate(25 28 69)" fill="${P.pink}" fill-opacity=".5"/><ellipse cx="100" cy="69" rx="7" ry="3" transform="rotate(-25 100 69)" fill="${P.pink}" fill-opacity=".5"/>` +
        `<path d="M64 38Q94 38 94 72Q92 108 64 110Q36 108 34 72Q34 38 64 38Z"${vol(p, c, P)}/>` +
        `<path d="M56 106Q60 124 64 128Q68 124 72 106Z" fill="${shade(c, 0.15)}" stroke="${edge(c, P)}" stroke-width="2" stroke-linejoin="round"/>` +
        eye(51, 70, 6, P) + eye(77, 70, 6, P) +
        `<path d="M59 88L69 88L64 93Z" fill="${P.pink}" stroke="${edge(P.pink, P)}" stroke-width="1.2" stroke-linejoin="round"/>` +
        cheeks(88, P) + smile(64, 97, 5, P)
      );
    },
  },
  // monkey — amber brown, big side ears, peach heart-shaped face
  z09: {
    c: (P) => shade(mix(P.orange, P.gold, 0.35), P.dark ? 0.38 : 0.28),
    draw: (P, p, c) => {
      const face = tint(P.orange, P.dark ? 0.62 : 0.6);
      return (
        body(`${p}x`, face, 0.4, 0.15) +
        `<circle cx="22" cy="72" r="15"${vol(p, c, P)}/><circle cx="106" cy="72" r="15"${vol(p, c, P)}/>` +
        `<circle cx="23" cy="72" r="8" fill="${face}"/><circle cx="105" cy="72" r="8" fill="${face}"/>` +
        `<circle cx="64" cy="72" r="42"${vol(p, c, P)}/>` +
        `<path d="M44 44Q52 36 58 42Q64 32 70 42Q76 36 84 44Q74 44 64 50Q54 44 44 44Z" fill="${shade(c, 0.3)}"/>` +
        `<path d="M64 60Q76 44 90 54Q100 68 88 84Q98 104 64 110Q30 104 40 84Q28 68 38 54Q52 44 64 60Z"${sub(`${p}x`, face, P, 1.5)}/>` +
        eye(52, 70, 6, P) + eye(76, 70, 6, P) +
        `<ellipse cx="60" cy="86" rx="2.2" ry="2.8" fill="${ink(P)}"/><ellipse cx="68" cy="86" rx="2.2" ry="2.8" fill="${ink(P)}"/>` +
        smile(64, 95, 10, P) +
        `<ellipse cx="44" cy="90" rx="6" ry="3.5" fill="${P.pink}" fill-opacity=".45"/><ellipse cx="84" cy="90" rx="6" ry="3.5" fill="${P.pink}" fill-opacity=".45"/>`
      );
    },
  },
  // rooster — golden yellow, red comb and wattle, orange beak, tail feathers
  z10: {
    c: (P) => (P.dark ? tint(P.gold, 0.2) : tint(sun(P), 0.1)),
    draw: (P, p, c) =>
      body(`${p}x`, P.red, 0.35, 0.3) +
      body(`${p}y`, P.orange, 0.4, 0.2) +
      `<path d="M92 96Q118 70 120 40Q106 58 96 64Q110 44 104 20Q96 50 84 64Z"${sub(`${p}y`, P.orange, P)}/>` +
      `<path d="M44 42Q38 26 48 24Q48 12 58 18Q62 4 72 16Q82 8 82 24Q92 26 84 42Z"${sub(`${p}x`, P.red, P)}/>` +
      `<circle cx="64" cy="76" r="38"${vol(p, c, P)}/>` +
      `<path d="M58 94Q58 112 64 114Q70 112 70 94Z"${sub(`${p}x`, P.red, P, 1.5)}/>` +
      `<path d="M52 82L76 82L64 98Z"${sub(`${p}y`, P.orange, P, 1.5)}/><path d="M53 86L75 86" stroke="${edge(P.orange, P)}" stroke-width="1.5"/>` +
      eye(49, 70, 6, P) + eye(79, 70, 6, P) +
      `<ellipse cx="38" cy="86" rx="6" ry="3.5" fill="${P.pink}" fill-opacity=".45"/><ellipse cx="90" cy="86" rx="6" ry="3.5" fill="${P.pink}" fill-opacity=".45"/>`,
  },
  // dog — light tan, darker floppy ears, cream muzzle, tongue (friendly, neutral)
  z11: {
    c: (P) => tint(P.orange, P.dark ? 0.42 : 0.38),
    draw: (P, p, c) => {
      const earC = shade(P.orange, P.dark ? 0.3 : 0.22);
      return (
        body(`${p}x`, earC, 0.3, 0.25) +
        `<circle cx="64" cy="74" r="40"${vol(p, c, P)}/>` +
        `<path d="M34 42Q14 44 14 78Q16 94 26 96Q36 90 40 66Q42 52 34 42Z"${sub(`${p}x`, earC, P)}/>` +
        `<path d="M94 42Q114 44 114 78Q112 94 102 96Q92 90 88 66Q86 52 94 42Z"${sub(`${p}x`, earC, P)}/>` +
        `<path d="M64 76Q86 76 88 96Q86 114 64 114Q42 114 40 96Q42 76 64 76Z" fill="${tint(P.gold, P.dark ? 0.75 : 0.82)}"/>` +
        `<path d="M58 100Q58 114 64 114Q70 114 70 100Z" fill="${P.pink}" stroke="${edge(P.pink, P)}" stroke-width="1.2"/>` +
        `<ellipse cx="64" cy="87" rx="8" ry="5.5" fill="${ink(P)}"/><ellipse cx="62" cy="85.5" rx="3" ry="1.6" fill="#FFFFFF" fill-opacity=".7"/>` +
        `<path d="M64 92V97M54 96Q64 106 74 96" fill="none" stroke="${ink(P)}" stroke-width="3" stroke-linecap="round"/>` +
        eye(49, 68, 6, P) + eye(79, 68, 6, P)
      );
    },
  },
  // pig — pink, folded triangle ears, big snout (friendly, neutral)
  z12: {
    c: (P) => P.pink,
    draw: (P, p, c) =>
      `<path d="M30 54L26 24L56 40Z"${vol(p, c, P)} stroke-linejoin="round"/><path d="M98 54L102 24L72 40Z"${vol(p, c, P)} stroke-linejoin="round"/>` +
      `<path d="M34 46L32 32L46 40Z M94 46L96 32L82 40Z" fill="${shade(c, 0.2)}" fill-opacity=".7"/>` +
      `<ellipse cx="64" cy="78" rx="44" ry="38"${vol(p, c, P)}/>` +
      body(`${p}x`, tint(c, 0.35), 0.4, 0.15) +
      `<ellipse cx="64" cy="90" rx="19" ry="13"${sub(`${p}x`, c, P)}/>` +
      `<ellipse cx="57" cy="90" rx="3.5" ry="5" fill="${shade(c, 0.5)}"/><ellipse cx="71" cy="90" rx="3.5" ry="5" fill="${shade(c, 0.5)}"/>` +
      eye(46, 70, 6, P) + eye(82, 70, 6, P) +
      `<ellipse cx="32" cy="90" rx="7" ry="4" fill="${tint(P.red, 0.3)}" fill-opacity=".4"/><ellipse cx="96" cy="90" rx="7" ry="4" fill="${tint(P.red, 0.3)}" fill-opacity=".4"/>` +
      smile(64, 108, 6, P),
  },
};
